import { VABLogoutQuery, ZoomServerSpec } from '../autogenerated/ZoomBridge';
import Authenticator from '../protocol/Authenticator';
import LoginQuery from '../queries/LoginQuery';
import ErrorHandler from '../error/ErrorHandler';
import GetCredentialsQuery from '../queries/GetCredentialsQuery';
import NotificationController from './NotificationController';
import ViewUtil from '../ViewUtil';
import BackgroundJobRunner from '../util/runner/BackgroundJobRunner';
import HttpOperation from '../protocol/HttpOperation';
import Store from '../store/Store';
import { MODE, SERVER_SPEC } from '../store/Constants';
import initVariables from '../store/init-legacy';
import t from '../i18n/I18n';
import SaveSettings from '../queries/settings/SaveSettings';
import ErrorId from '../protocol/ErrorId';
import PreviewController from './preview/PreviewController';
import System from '../basic/System';
import Storage from '../protocol/Storage';

const debug = require('../debug/debug')('wc:home:AuthenticationController');

export default class AuthenticationController {
  static setupOnAuthFailure() {
    if (AuthenticationController.SETUP_DONE) return;

    const authCallback = (errorId) => {
      //All auth failure calls will come here.
      //Show login screen.
      /**
       * @type{ErrorId}
       */
      const error = errorId;
      /***
       * When authentication will fail for all the queries....
       * We have to the login screen...
       */
      AuthenticationController.resetLoginSession();
    };
    Authenticator.getInstance().setOnAuthFailureCallback(authCallback);
    AuthenticationController.SETUP_DONE = true;
  }

  static logout(callback: Func) {
    const logout = new VABLogoutQuery();
    logout.message = System.getAppName() + ' logout';
    logout.run().then(
      (resp) => {
        return callback(resp);
      },
      (error) => {
        ErrorHandler.handleError(error);
        return callback(null);
      }
    );
  }

  static onLogoutResp() {
    AuthenticationController.resetLoginSession();
    //window.setARGS({}, true);
    //location.reload();
  }

  static onSuccessfullLogin() {
    AuthenticationController.updateSessionSettings();
  }

  static removeCorruptViewProps() {
    let totalViewProps = [];
    if (window.gs.propName2PropInfoMap && window.gs.staticPropsArray) {
      let allViewPropsArray = window.gs.propName2PropInfoMap.toArray();
      allViewPropsArray.forEach((propInfo) => {
        totalViewProps.push(propInfo.key);
      });
      totalViewProps = totalViewProps.concat(window.gs.staticPropsArray);

      window.gs.viewProps = window.gs.viewProps.filter((key) => totalViewProps.includes(key));
    }
  }

  static updateSessionSettings() {
    //SaveSettings.getAsync(MODE, false).then(
    //  (value: ?string) => {
    let viewPropsArray: Array<string> = [];
    window.gs.staticPropsArray = ['status', 'updated', 'smartCopy/Link', 'high-res path'];
    window.gs.commonForBothViewStaticCol = ['author', 'versions', 'repo revision'];
    const viewProps: ?string = SaveSettings.get(SaveSettings.VIEWPROPS);
    if (viewProps) {
      viewPropsArray = viewProps.split(',').map(function(value) {
        return value.trim();
      });
    }

    if (!viewPropsArray.includes('status')) {
      viewPropsArray.push('status');
    }
    window.gs.viewProps = viewPropsArray;
    this.removeCorruptViewProps();
    let advMDGroupSortingOrder = null;
    let advMDGroupToHide = null;
    //advMDGroupSortingOrder = webClientSettings.getAdvMDGroupSortingOrder();
    //advMDGroupToHide = webClientSettings.getAdvMDGroupToHide();
    window.gs.advMDGroupSortingOrder = advMDGroupSortingOrder != null ? advMDGroupSortingOrder : [];
    window.gs.advMDGroupToHide = advMDGroupToHide != null ? advMDGroupToHide : [];
    //SaveSettings.SETTINGS_CACHE.set(MODE, value);
    let log_level = SaveSettings.get(SaveSettings.LOG_LEVEL);
    if (!log_level) {
      log_level = 'INFO';
    } else {
      log_level = log_level.toUpperCase();
    }
    localStorage.setItem(SaveSettings.LOG_LEVEL, log_level);
    //  },
    //  (errorId: ErrorId) => {
    //   if (debug.enabled) {
    //     debug('[updateSessionSettings()] errorId: %s', errorId);
    //   }
    //  }
    // );
  }

  static updateSettings() {
    SaveSettings.updateSettings().then(
      () => {
        BackgroundJobRunner.updateConfigs(
          SaveSettings.getBoolean('REFRESH_ON_FOCUS'),
          SaveSettings.get('NOTIFICATION_UPDATE_TIME_IN_SEC')
        );
        AuthenticationController.onSuccessfullLogin();
        window.render();
      },
      (error) => {
        console.log(error);
        ErrorHandler.handleError(error);
      }
    );
  }

  static signin(
    username: string,
    password: string,
    resetOnFail = true,
    passwordIsCookie: boolean = false,
    licenseId: ?string = null
  ) {
    const query = new LoginQuery(username, password, passwordIsCookie, licenseId);
    query.licenseId = licenseId;
    window.gs.loginInProgress = true;
    return query.run().then(
      (resp) => {
        this.onSignInSuccess(System.isBrowserApp() ? AuthenticationController.getZoomServerSpec(resp) : resp);
      },
      (error) => {
        if (error) {
          console.log(error);
          if (ViewUtil.isDirectLinkToCollection()) {
            ErrorHandler.handleError(t('LINK_SHARING_DISABLED'));
          } else {
            ErrorHandler.handleError(error);
          }
        } else {
          console.error(error);
          ErrorHandler.handleError(t('UNKNOWN_ERROR'));
        }

        if (resetOnFail) {
          AuthenticationController.setLoggedInFail();
        }
      },
      () => {
        window.gs.loginInProgress = false;
      }
    );
  }

  static onSignInSuccess(zoomServerSpec: ZoomServerSpec) {
    AuthenticationController.updateSessionData(zoomServerSpec);
    this.sendAuthRequestOnReviewServer(true)
      .then()
      .finally(() => {
        if (!window.gs.pageReload) {
          this.updateSettings();
        } else {
          delete window.gs.pageReload;
        }
      });
  }

  static setLoggedInFail() {
    AuthenticationController.resetLoginSession(true);
  }

  static onLoginSuccess(resp) {
    if (HttpOperation.checkForAuthFailure(200, resp)) {
      debug('[issueGetCredentials()] Auth failure: ', resp);
      AuthenticationController.setLoggedInFail();
    } else {
      this.updateSettings();
    }
  }

  static sendAuthRequestOnReviewServer(displayError: boolean) {
    return new Promise(async (resolve, reject) => {
      if (System.isBrowserApp()) {
        await PreviewController.issuePreviewServerAuth(displayError);
        resolve(true);
      } else {
        PreviewController.issuePreviewServerAuth(displayError);
        resolve(true);
      }
    });
  }

  static issueGetCredentials() {
    AuthenticationController.globalCount = 0;
    const timer = setTimeout(() => {
      window.render();
    }, 3000);

    new GetCredentialsQuery(Authenticator.AUTH_DATA.username, Authenticator.AUTH_DATA.cookie).run().then(
      (resp) => {
        clearTimeout(timer);
        AuthenticationController.updateSessionData(resp);
        this.sendAuthRequestOnReviewServer(false)
          .then()
          .finally(() => {
            this.onLoginSuccess(resp);
          });
      },
      (error) => {
        if (System.isDesktop() && error && error.errorId === 408) {
          if (this.autoLoginTimerId) clearTimeout(this.autoLoginTimerId);
          ErrorHandler.handleError(error);
        }
        clearTimeout(timer);
        AuthenticationController.setLoggedInFail();
      }
    );
  }

  static updateSessionData(resp) {
    const serverSpec: ZoomServerSpec = resp;
    const authData = Authenticator.AUTH_DATA;
    authData.username = serverSpec.getZmUserName();
    authData.externalUserId = serverSpec.getExternalUserId();
    authData.externalUserId = serverSpec.getExternalUserId();
    authData.password = null;
    if (authData.cookie === serverSpec.getZmCookie()) {
      window.gs.sameCookieFound = true;
    }
    authData.cookie = serverSpec.getZmCookie();
    authData.repoId = serverSpec.getRepoId();
    authData.serverUrl = serverSpec.getZmServer();
    authData.userHasAdminPerm = serverSpec.isUserHasAdminPerm();
    authData.clientSessionId = serverSpec.getSessionId();
    authData.reviewServerUrl = serverSpec.rv_server;
    authData.userHasAdminPerm = serverSpec.isUserHasAdminPerm();
    authData.sessionId = serverSpec.getSessionId();
    authData.isSuperAdmin = serverSpec.isIsSuperAdmin();
    const reviewServerHostPortList = serverSpec.getReviewServerHostPortList();
    authData.previewServerUrl =
      reviewServerHostPortList && reviewServerHostPortList.length > 0 ? reviewServerHostPortList[0] : null;

    authData.sslRvEnabled = resp.isSslRv();
    authData.previewServerUrlsList =
      resp.getReviewServerHostPortList() && resp.getReviewServerHostPortList().length > 0
        ? resp.getReviewServerHostPortList()
        : null;
    authData.previewServerSSLUrlsList =
      resp.getReviewServerHostPortSSLList() && resp.getReviewServerHostPortSSLList().length > 0
        ? resp.getReviewServerHostPortSSLList()
        : null;
    if (authData.sslRvEnabled) {
      authData.previewServerUrl =
        authData.previewServerSSLUrlsList && authData.previewServerSSLUrlsList.length > 0
          ? authData.previewServerSSLUrlsList[0]
          : null;
    } else {
      authData.previewServerUrl =
        authData.previewServerUrlsList && authData.previewServerUrlsList.length > 0
          ? authData.previewServerUrlsList[0]
          : null;
    }

    authData.webminUrl = resp.getWebminServer();
    Authenticator.getInstance().runAuthFailedRequests();
    window.gs.companyTagLine = serverSpec.getCompanyTagLine();
    window.gs.companyName = serverSpec.getCompanyName();
    window.gs.version = serverSpec.getVersion();
    window.gs.isExternalUser = serverSpec.isIsExternalUser();
    window.gs.isNoRoleUser = serverSpec.isShowOnlyCollection();
    window.gs.serverRealm = resp.getServerRealm();
    window.gs.storageTypes = resp.getStorageTypes();
    window.gs.defaultCheckoutPath = resp.getDefaultCheckoutPath ? resp.getDefaultCheckoutPath() : null;
    Store.put(SERVER_SPEC, serverSpec);
    if (resp.getExtraProps()) {
      if (!Storage.getSessionStorage('AUTO_TPM_SHOWN')) {
        const isValidTPM = resp.getExtraProps().get('VALID_TPM_EXISTS');
        if (!isValidTPM || isValidTPM === 'false') {
          window.gs.isOpenTPM = true;
          Storage.setSessionStorage('AUTO_TPM_SHOWN', true);
        }
      }
    }
    AuthenticationController.setLoggedinState(true);

    setTimeout(() => {
      ViewUtil.setTitle();
    }, 2000);
  }

  static getZoomServerSpec(resp): ZoomServerSpec {
    const serverSpec = resp;
    const zoomServerSpec = new ZoomServerSpec();
    zoomServerSpec.setZmUserName(serverSpec.zm_username);
    zoomServerSpec.setZmPassword(null);
    zoomServerSpec.setZmCookie(serverSpec.zm_cookie);
    zoomServerSpec.setRepoId(serverSpec.zm_repo_id);
    zoomServerSpec.setZmServer(serverSpec.zm_server);
    zoomServerSpec.setUserHasAdminPerm(serverSpec.has_admin_right === 'true');
    zoomServerSpec.setWebminServer(serverSpec.wbm_server);
    zoomServerSpec.setCompanyName(serverSpec.company_name);
    zoomServerSpec.setCompanyTagLine(serverSpec.company_tag_line);
    zoomServerSpec.setVersion(serverSpec.version);
    const isExternalUser = serverSpec.show_only_coll ? serverSpec.is_external_user.toLowerCase() : false;
    const showOnlyCollection = serverSpec.show_only_coll ? serverSpec.show_only_coll.toLowerCase() : false;
    zoomServerSpec.setIsExternalUser(isExternalUser === 'true');
    zoomServerSpec.setShowOnlyCollection(showOnlyCollection === 'true');
    zoomServerSpec.setServerRealm(serverSpec.svr_realm);
    zoomServerSpec.setReviewServerHostPortList([resp.rv_server]);
    zoomServerSpec.setIsSuperAdmin(serverSpec.is_superadmin === 'true');
    zoomServerSpec.setExternalUserId(serverSpec.externalUserId);
    return zoomServerSpec;
  }

  static resetLoginSession(ignoreReInit) {
    AuthenticationController.setLoggedinState(false);
    if (!ignoreReInit) initVariables();
    //window.gs.cookie = null;
    window.gs.serverUrl = null;
    window.gs.sessionId = null;
    window.gs.sessionIdKey = null;
    window.gs.loading = false;
    window.gs.progress = null;
    //localStorage.setItem("zm_username", null);
    //localStorage.setItem("zm_cookie", null);
    //localStorage.setItem("zm_client_session_id", null);
    const authData = Authenticator.AUTH_DATA;
    //authData.username = null;
    authData.password = null;
    authData.cookie = null;
    authData.repoId = null;
    authData.serverUrl = null;
    authData.userHasAdminPerm = undefined;
    //authData.sessionId = null;
    window.render();
  }

  static setLoggedinState(val) {
    window.gs.isLogged = val;
    if (val) {
      BackgroundJobRunner.JOB_RUNNER.resume();
      NotificationController.getInstance().resetTimer();
      clearTimeout(this.autoLoginTimerId);
      this.autoLoginTimerId = 0;
    } else {
      BackgroundJobRunner.JOB_RUNNER.pause('User not logged in.');
      NotificationController.getInstance().clearTimer();
      if (System.isDesktop()) {
        this.autoLoginTimerId = setTimeout(
          AuthenticationController.retryLogin,
          5000 * (AuthenticationController.globalCount < 50 ? 1 : Math.round(AuthenticationController.globalCount / 50))
        );
      }
    }
  }

  static retryLogin() {
    if (window.gs.loginInProgress) {
      if (this.autoLoginTimerId) clearTimeout(this.autoLoginTimerId);
      this.autoLoginTimerId = setTimeout(
        AuthenticationController.retryLogin,
        5000 * (AuthenticationController.globalCount < 50 ? 1 : Math.round(AuthenticationController.globalCount / 50))
      );
      return;
    }
    AuthenticationController.globalCount++;
    if (this.autoLoginTimerId) clearTimeout(this.autoLoginTimerId);
    new GetCredentialsQuery(Authenticator.AUTH_DATA.username, Authenticator.AUTH_DATA.cookie).run().then(
      (resp) => {
        AuthenticationController.updateSessionData(resp);
        AuthenticationController.sendAuthRequestOnReviewServer(false)
          .then()
          .finally(() => {
            AuthenticationController.onLoginSuccess(resp);
          });
      },
      (error) => {
        if (System.isDesktop() && error && error.errorId === 408) {
          if (this.autoLoginTimerId) clearTimeout(this.autoLoginTimerId);
          ErrorHandler.handleError(error);
        } else AuthenticationController.setLoggedInFail();
      }
    );
  }

  static SETUP_DONE: boolean;
  static autoLoginTimerId = 0;
}

/**
 * @private
 */
AuthenticationController.SETUP_DONE = false;
